import { useEffect, useState } from "react";
import { Plus, Trash2, Save, Copy } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { SectionTitle, Card } from "../ui";

const FIELD = "w-full bg-white border border-sand-200 rounded-lg px-3 py-2 text-sm text-charcoal-800 focus:outline-none focus:ring-2 focus:ring-sage/50";
const blank = {
  name: "", description: "", duration_minutes: 45, deposit_amount: 0, buffer_minutes: 15, active: true, order: 99,
  available_days: [], blackout_dates: [], meeting_methods: ["video", "phone", "whatsapp_call", "whatsapp_video", "in_person"],
  meeting_links: {}, location_address: "", min_notice_hours: 12, max_per_day: 0, intake_questions: [],
  confirmation_text: "", cancel_policy: "", staff_email: "", slot_increment_minutes: 0,
};
const DAYS = [["Mon", 0], ["Tue", 1], ["Wed", 2], ["Thu", 3], ["Fri", 4], ["Sat", 5], ["Sun", 6]];
const METHOD_LABELS = {
  video: "Video (in-site)", phone: "Phone", zoom: "Zoom", google_meet: "Google Meet", teams: "Teams",
  whatsapp_call: "WhatsApp call", whatsapp_video: "WhatsApp video", facetime: "FaceTime",
  in_person: "In person", invitee_location: "Client chooses place", custom: "Custom link",
};
const LINK_METHODS = ["zoom", "google_meet", "teams", "custom"];
const toggle = (arr, v) => ((arr || []).includes(v) ? (arr || []).filter((x) => x !== v) : [...(arr || []), v]);

function TypeSchedule({ obj, onChange }) {
  return (
    <div className="sm:col-span-2 space-y-2 border-t border-sand-200 pt-3">
      <p className="text-sm text-charcoal-600">Available days <span className="text-charcoal-400">(none selected = every available day)</span></p>
      <div className="flex flex-wrap gap-1.5">
        {DAYS.map(([lbl, d]) => (
          <button type="button" key={d} data-testid={`apptype-day-${d}`} onClick={() => onChange("available_days", toggle(obj.available_days, d))}
            className={`rounded-full px-3 py-1 text-xs border transition-colors ${(obj.available_days || []).includes(d) ? "bg-sage text-white border-sage" : "bg-white text-charcoal-600 border-sand-200 hover:border-sage"}`}>{lbl}</button>
        ))}
      </div>
      <label className="text-sm text-charcoal-600 block">Blackout dates (comma-separated YYYY-MM-DD)
        <input className={FIELD} placeholder="2026-12-25, 2027-01-01" value={(obj.blackout_dates || []).join(", ")}
          onChange={(e) => onChange("blackout_dates", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} />
      </label>
    </div>
  );
}

function TypeAdvanced({ obj, onChange }) {
  const methods = obj.meeting_methods || [];
  const questions = obj.intake_questions || [];
  return (
    <div className="sm:col-span-2 space-y-4 border-t border-sand-200 pt-4">
      <div>
        <p className="text-sm text-charcoal-600 mb-2">Meeting methods clients can choose</p>
        <div className="flex flex-wrap gap-1.5">
          {Object.entries(METHOD_LABELS).map(([m, lbl]) => (
            <button type="button" key={m} data-testid={`apptype-method-${m}`} onClick={() => onChange("meeting_methods", toggle(methods, m))}
              className={`rounded-full px-3 py-1 text-xs border transition-colors ${methods.includes(m) ? "bg-terracotta text-white border-terracotta" : "bg-white text-charcoal-600 border-sand-200 hover:border-terracotta"}`}>{lbl}</button>
          ))}
        </div>
      </div>
      {LINK_METHODS.filter((m) => methods.includes(m)).map((m) => (
        <label key={m} className="text-sm text-charcoal-600 block">{METHOD_LABELS[m]} link
          <input className={FIELD} placeholder={`https://… your ${METHOD_LABELS[m]} meeting link`} value={obj.meeting_links?.[m] || ""}
            onChange={(e) => onChange("meeting_links", { ...(obj.meeting_links || {}), [m]: e.target.value })} />
        </label>
      ))}
      {methods.includes("in_person") && (
        <label className="text-sm text-charcoal-600 block">In-person address
          <input className={FIELD} placeholder="Calle 60 #123, Centro, Mérida, Yucatán" value={obj.location_address || ""}
            onChange={(e) => onChange("location_address", e.target.value)} />
        </label>
      )}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <label className="text-sm text-charcoal-600">Time slot increments
          <select data-testid="apptype-increment" className={FIELD} value={obj.slot_increment_minutes ?? 0} onChange={(e) => onChange("slot_increment_minutes", Number(e.target.value))}>
            <option value={0}>Default (global setting)</option>
            {[15, 30, 45, 60].map((n) => <option key={n} value={n}>{n} min</option>)}
          </select></label>
        <label className="text-sm text-charcoal-600">Buffer between bookings (min)
          <input type="number" className={FIELD} value={obj.buffer_minutes ?? 0} onChange={(e) => onChange("buffer_minutes", e.target.value)} /></label>
        <label className="text-sm text-charcoal-600">Minimum notice (hours)
          <input type="number" className={FIELD} value={obj.min_notice_hours ?? 12} onChange={(e) => onChange("min_notice_hours", e.target.value)} /></label>
        <label className="text-sm text-charcoal-600">Max bookings/day (0 = unlimited)
          <input type="number" className={FIELD} value={obj.max_per_day ?? 0} onChange={(e) => onChange("max_per_day", e.target.value)} /></label>
      </div>
      <label className="text-sm text-charcoal-600 block">Assigned staff email (default)
        <input className={FIELD} placeholder="info@holamovers.com" value={obj.staff_email || ""} onChange={(e) => onChange("staff_email", e.target.value)} />
      </label>
      <div>
        <p className="text-sm text-charcoal-600 mb-2">Intake questions</p>
        {questions.map((q, i) => (
          <div key={i} className="mb-2 flex items-center gap-2">
            <input className={FIELD} placeholder="Question label" value={q.label || ""}
              onChange={(e) => onChange("intake_questions", questions.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} />
            <label className="inline-flex shrink-0 items-center gap-1.5 text-xs text-charcoal-600">
              <input type="checkbox" checked={!!q.required}
                onChange={(e) => onChange("intake_questions", questions.map((x, j) => (j === i ? { ...x, required: e.target.checked } : x)))} /> required
            </label>
            <button type="button" onClick={() => onChange("intake_questions", questions.filter((_, j) => j !== i))}
              className="shrink-0 rounded p-1.5 text-charcoal-400 hover:text-destructive"><Trash2 size={14} /></button>
          </div>
        ))}
        <button type="button" data-testid="apptype-add-question" onClick={() => onChange("intake_questions", [...questions, { label: "", required: false }])}
          className="rounded-full border border-sand-200 px-3 py-1 text-xs text-charcoal-600 hover:border-sage">+ Add question</button>
      </div>
      <label className="text-sm text-charcoal-600 block">Confirmation message (added to the confirmation email)
        <textarea rows={2} className={FIELD} value={obj.confirmation_text || ""} onChange={(e) => onChange("confirmation_text", e.target.value)} />
      </label>
      <label className="text-sm text-charcoal-600 block">Cancel / reschedule policy
        <textarea rows={2} className={FIELD} placeholder="Free reschedule up to 24h before. Deposits refundable up to 48h before." value={obj.cancel_policy || ""} onChange={(e) => onChange("cancel_policy", e.target.value)} />
      </label>
    </div>
  );
}

export default function AppointmentTypesManager() {
  const [rows, setRows] = useState([]);
  const [neu, setNeu] = useState(blank);
  const [expanded, setExpanded] = useState(null);

  const load = () => api.get("/admin/appointment-types").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!neu.name.trim()) return toast.error("Name is required");
    try { await api.post("/admin/appointment-types", neu); toast.success("Appointment type added"); setNeu(blank); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not add"); }
  };
  const save = async (t) => {
    try { await api.put(`/admin/appointment-types/${t.id}`, t); toast.success("Saved"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
  };
  const duplicate = async (t) => {
    try { await api.post(`/admin/appointment-types/${t.id}/duplicate`); toast.success("Duplicated (inactive copy created)"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not duplicate"); }
  };
  const remove = async (t) => {
    if (!window.confirm(`Delete "${t.name}"?`)) return;
    try { await api.delete(`/admin/appointment-types/${t.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not delete"); }
  };
  const edit = (id, k, v) => setRows((rs) => rs.map((r) => (r.id === id ? { ...r, [k]: v } : r)));

  return (
    <div data-testid="admin-appointment-types">
      <SectionTitle title="Appointment Types" subtitle="Define bookable services — duration, deposit, meeting methods, availability rules and intake questions." />

      <Card className="mb-6">
        <h3 className="font-medium text-charcoal-800 mb-4">Add a type</h3>
        <div className="grid gap-3 sm:grid-cols-2">
          <input data-testid="apptype-new-name" className={FIELD} placeholder="Name (e.g. Pet transport call)" value={neu.name} onChange={(e) => setNeu({ ...neu, name: e.target.value })} />
          <input className={FIELD} placeholder="Short description" value={neu.description} onChange={(e) => setNeu({ ...neu, description: e.target.value })} />
          <label className="text-sm text-charcoal-600">Duration (min)<input type="number" className={FIELD} value={neu.duration_minutes} onChange={(e) => setNeu({ ...neu, duration_minutes: e.target.value })} /></label>
          <label className="text-sm text-charcoal-600">Deposit ($) — collected before confirmation<input type="number" className={FIELD} value={neu.deposit_amount} onChange={(e) => setNeu({ ...neu, deposit_amount: e.target.value })} /></label>
          <TypeSchedule obj={neu} onChange={(k, v) => setNeu({ ...neu, [k]: v })} />
          <TypeAdvanced obj={neu} onChange={(k, v) => setNeu({ ...neu, [k]: v })} />
        </div>
        <button data-testid="apptype-add" onClick={add} className="mt-4 inline-flex items-center gap-2 rounded-full bg-terracotta px-5 py-2 text-sm text-white font-medium hover:bg-terracotta-hover"><Plus size={16} /> Add type</button>
      </Card>

      <div className="space-y-4">
        {rows.map((t) => (
          <Card key={t.id}>
            <div className="grid gap-3 sm:grid-cols-2">
              <input className={FIELD} value={t.name} onChange={(e) => edit(t.id, "name", e.target.value)} />
              <input className={FIELD} value={t.description || ""} onChange={(e) => edit(t.id, "description", e.target.value)} />
              <label className="text-sm text-charcoal-600">Duration (min)<input type="number" className={FIELD} value={t.duration_minutes} onChange={(e) => edit(t.id, "duration_minutes", e.target.value)} /></label>
              <label className="text-sm text-charcoal-600">Deposit ($) — collected before confirmation<input type="number" className={FIELD} value={t.deposit_amount} onChange={(e) => edit(t.id, "deposit_amount", e.target.value)} /></label>
              <TypeSchedule obj={t} onChange={(k, v) => edit(t.id, k, v)} />
              {expanded === t.id && <TypeAdvanced obj={t} onChange={(k, v) => edit(t.id, k, v)} />}
            </div>
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-4">
                <label className="inline-flex items-center gap-2 text-sm text-charcoal-600">
                  <input type="checkbox" checked={t.active} onChange={(e) => edit(t.id, "active", e.target.checked)} /> Active
                </label>
                <button data-testid={`apptype-expand-${t.id}`} onClick={() => setExpanded(expanded === t.id ? null : t.id)}
                  className="text-sm text-terracotta underline underline-offset-2">{expanded === t.id ? "Hide advanced settings" : "Advanced settings"}</button>
              </div>
              <div className="flex gap-2">
                <button data-testid={`apptype-duplicate-${t.id}`} onClick={() => duplicate(t)} title="Duplicate" className="rounded-lg p-2 text-charcoal-400 hover:bg-sand-100 hover:text-charcoal-700"><Copy size={16} /></button>
                <button data-testid={`apptype-save-${t.id}`} onClick={() => save(t)} className="inline-flex items-center gap-1.5 rounded-full bg-sage px-4 py-2 text-sm text-white font-medium hover:bg-sage-hover"><Save size={15} /> Save</button>
                <button data-testid={`apptype-delete-${t.id}`} onClick={() => remove(t)} className="rounded-lg p-2 text-charcoal-400 hover:bg-sand-100 hover:text-destructive"><Trash2 size={16} /></button>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
